/*
  Sableau — show the theme's fixed euro amounts in the visitor's own currency.

  Shopify converts anything that comes from a Shopify object (product prices,
  cart totals), but not amounts typed into theme settings. A Norwegian visitor
  therefore read "kr 1.110,33" for the shoe and, three lines below it,
  "Shipping €6.95 · free over €79" — two currencies on the same screen at the
  moment of buying.

  Liquid cannot fix this: raw integers passed to `money` are formatted with the
  presentment symbol but never converted, which is why the cart progress bar
  hides itself outside the euro zone. The storefront does expose the rate as
  `Shopify.currency.rate`, so the substitution happens here instead.

  It used to do a second job: swap the EU fee for the higher International rate,
  because Norway, the UK and the US pay more below the free-shipping threshold.
  That swap was removed on 2026-08-20. The store now states one shipping figure
  to every country, so the only thing left to do here is convert it.

  Amounts are matched against a fixed list, so prose that happens to contain a
  price (a review saying "never €900 worth of wanting") is left alone.
*/
(function () {
  'use strict';

  /* Read on every pass rather than once at parse time. A deferred script can run
     against a cached head where Shopify.currency is still the shop default, and a
     single early read then locks the whole page into euros. */
  var active = 'EUR';
  var rate = 1;
  var isEuZone = true;

  function readContext() {
    var shop = window.Shopify || {};
    active = (shop.currency && shop.currency.active) || 'EUR';
    rate = parseFloat((shop.currency && shop.currency.rate) || '1') || 1;
    var code = (shop.country || '').toUpperCase();
    // Kept for window.__localMoney, which the drawer script reads to decide
    // nothing about price any more — only to describe the visitor.
    isEuZone = !code || EU_ZONE.indexOf(code) !== -1;
    window.__localMoney = { active: active, rate: rate, country: code, euZone: isEuZone };
  }

  // The zone list is still read for window.__localMoney, which the drawer
  // script uses. It no longer changes the fee: as of 2026-08-20 every country
  // reads the same 6.95, on the operator's instruction, so that one shipping
  // figure travels the whole storefront.
  var EU_ZONE = ('NL,AT,BE,BG,HR,CY,CZ,DE,DK,EE,FI,FR,GR,HU,IE,IT,LV,LT,LU,MT,' +
    'PL,PT,RO,SK,SI,ES,SE').split(',');

  // Currencies whose everyday amounts carry no decimals.
  var WHOLE_UNIT = ['NOK', 'SEK', 'DKK', 'ISK', 'JPY', 'HUF', 'CZK', 'KRW', 'CLP'];
  var SYMBOL = {
    EUR: '€', GBP: '£', USD: '$', AUD: '$', CAD: '$', NZD: '$',
    NOK: 'kr', SEK: 'kr', DKK: 'kr', ISK: 'kr', CHF: 'CHF', PLN: 'zł',
    CZK: 'Kč', JPY: '¥', HUF: 'Ft',
  };

  /* 'threshold' rounds up to a clean number so we never promise free shipping
     below the real cut-off — being asked for a little too much and then getting
     it free anyway is a good surprise; the reverse is a broken promise.
     'fee' rounds up as well, so a cost is never understated. */
  var AMOUNTS = {
    '79': 'threshold',
    '35': 'threshold',
    '6.95': 'fee',
    '4.95': 'fee',
    '19.95': 'fee',
    '24.95': 'fee',
    '2.95': 'fee',
  };

  function roundUp(value, step) {
    return Math.ceil(value / step) * step;
  }

  function convert(eur, kind) {
    var value = eur * rate;
    if (kind === 'threshold') {
      return value >= 100 ? roundUp(value, 50) : roundUp(value, 5);
    }
    if (WHOLE_UNIT.indexOf(active) !== -1) return roundUp(value, 1);
    return roundUp(value * 20, 1) / 20; // fees land on the next 0.05
  }

  function format(value) {
    var decimals = WHOLE_UNIT.indexOf(active) !== -1 || value === Math.round(value) ? 0 : 2;
    var text;
    try {
      text = new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch (e) {
      text = value.toFixed(decimals);
    }
    return (SYMBOL[active] || active) + ' ' + text;
  }

  // Reviews and testimonials quote prices as prose; never touch those.
  var SKIP = 'blockquote, .brand-reviews, [class*="testimonial"], [class*="review"],' +
    ' script, style, noscript, textarea';

  var PATTERN = /€\s?(\d{1,3}(?:[.,]\d{2})?)/g;

  function replaceIn(node) {
    var text = node.nodeValue;
    if (!text || text.indexOf('€') === -1) return;
    var out = text.replace(PATTERN, function (whole, digits) {
      var key = digits.replace(',', '.');
      var kind = AMOUNTS[key];
      if (!kind) return whole; // not one of ours — leave it exactly as written
      var eur = parseFloat(key);
      return format(convert(eur, kind));
    });
    if (out !== text) node.nodeValue = out;
  }

  function walk(root) {
    if (!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue || n.nodeValue.indexOf('€') === -1) return NodeFilter.FILTER_REJECT;
        if (n.parentElement && n.parentElement.closest(SKIP)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    var pending = [];
    var node;
    while ((node = walker.nextNode())) pending.push(node);
    pending.forEach(replaceIn);
  }

  /* Sections are web components that re-render themselves, sometimes writing the
     original text straight back over ours. A single pass at DOMContentLoaded won
     the race on one country and lost it on the next, so re-run on a short ladder
     and keep watching afterwards. `busy` stops our own edits re-triggering the
     observer. */
  /* Nothing to rewrite when the amounts are already in the currency they were
     written in and the shipping fee is the one this zone actually pays. Checking
     the rate as well as the code stops a euro page being "converted" to itself,
     which rounded 79 up to 80. */
  function nothingToDo() {
    return active === 'EUR' && rate === 1 && isEuZone;
  }

  var busy = false;
  function run() {
    if (busy) return;
    readContext();
    if (nothingToDo()) return;
    busy = true;
    try { walk(document.body); } finally { busy = false; }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
  [200, 600, 1500, 3000].forEach(function (ms) { setTimeout(run, ms); });

  // The cart drawer, quick-add modal and size dialogs render after load.
  new MutationObserver(function (records) {
    if (busy) return;
    readContext();
    // Guard the whole callback: without this a re-rendering section on a euro
    // page went through walk() and "converted" 79 into 80.
    if (nothingToDo()) return;
    for (var i = 0; i < records.length; i++) {
      var record = records[i];
      if (record.type === 'characterData') {
        busy = true;
        try { replaceIn(record.target); } finally { busy = false; }
        continue;
      }
      var added = record.addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) walk(added[j]);
        else if (added[j].nodeType === 3) {
          busy = true;
          try { replaceIn(added[j]); } finally { busy = false; }
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();
